import { Component, Input, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/internal/operators/take';
import { ManagementMediumClippingControllerService, ManagementRecordingScheduleControllerService, ManagementRecordingScheduleWithMediumResponse } from 'api-clipping';
import { RecordingService, RecordingSettings } from '../../../../shared/services/recording.service';
import { ConfirmationDialogComponent } from '../../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { UtilsService } from '../../../../shared/services/utils.service';
import { NavMapComponent } from '../../../../shared/components/nav-map/nav-map.component';
import { MediumComponent } from '../medium/medium.component';

interface MediumRow {
	mediumId: string;
	name: string;
	type: string;
}

interface ScheduleBlock {
	id: string;
	mediumId: string;
	startMinutes: number;
	endMinutes: number;
	enabled: boolean;
	// Username parsed server-side from the medium's IPTV link (never the password) - null means
	// the link has no recognizable username, rendered grey and never compared for overlap. See
	// В1 in docs/TV-MEDIUMI-AVTOMATIZACIJA.md.
	iptvUsername: string | null;
}

interface ConflictRange {
	startMinutes: number;
	endMinutes: number;
}

const MINUTES_IN_DAY = 24 * 60;

// Stable palette for IPTV-username block colour (В1) - distinct from each other and from the two
// reserved colours (conflict outline red, "без претплата" grey), never read elsewhere.
const USERNAME_PALETTE = ['#1e88e5', '#43a047', '#f9a825', '#8e24aa', '#00897b', '#5c6bc0', '#d81b60', '#6d4c41', '#00acc1', '#7cb342'];
const NO_SUBSCRIPTION_COLOR = '#9e9e9e';

// Width of the timeline (everything right of the 180px label column) at zoom 1x. Together with the
// label column this is the chart's original 1100px min-width; zoom only multiplies this part.
const TIMELINE_BASE_WIDTH_PX = 920;
// Rough widths of the in-block time label at 10px: "12:00–12:20" and "12:00".
const LABEL_RANGE_MIN_PX = 70;
const LABEL_START_MIN_PX = 32;

@Component({
	selector: 'app-recording-schedule-gantt',
	standalone: true,
	imports: [CommonModule, FormsModule, MatCardModule, MatDividerModule, MatIconModule, MatTooltipModule, MatButtonToggleModule, MatProgressSpinnerModule, MatSlideToggleModule, TranslateModule, NavMapComponent],
	templateUrl: './recording-schedule-gantt.component.html',
	styleUrl: './recording-schedule-gantt.component.scss',
})
export class RecordingScheduleGanttComponent implements OnInit {
	// Shown inside Settings: no page padding, card or breadcrumb (Settings has its own).
	@Input() embedded = false;

	readonly hours = Array.from({ length: 24 }, (_, i) => i);
	readonly daysOfWeek = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
	readonly zoomLevels = [1, 2, 4];
	// Every 15 minutes, skipping full hours (those already have an hour gridline).
	readonly quarterMinutes = Array.from({ length: 24 * 4 }, (_, i) => i * 15).filter(minute => minute % 60 !== 0);

	@ViewChild('ganttScroll') ganttScroll?: ElementRef<HTMLElement>;

	zoom = 1;

	selectedDay = this.currentDayOfWeek();
	loading = false;
	maxConcurrentRecordings = 1;
	// Server-side recording master switch (recording_settings.recording_enabled).
	recordingEnabled = false;
	// AI segmentation after recording (recording_settings.segmentation_enabled).
	segmentationEnabled = true;
	// AI transcription after upload (recording_settings.transcription_enabled).
	transcriptionEnabled = true;
	// Automatic clip creation after a successful transcription (recording_settings.auto_create_clips_enabled).
	autoCreateClipsEnabled = false;
	savingSwitch = false;

	mediumRows: MediumRow[] = [];
	private allSchedules: ManagementRecordingScheduleWithMediumResponse[] = [];
	private conflictRanges: ConflictRange[] = [];
	private conflictScheduleIds = new Set<string>();

	constructor(
		public utils: UtilsService,
		private apiRecordingSchedule: ManagementRecordingScheduleControllerService,
		private apiMedium: ManagementMediumClippingControllerService,
		private recordingService: RecordingService,
		private dialog: MatDialog,
		private translate: TranslateService
	) {}

	ngOnInit(): void {
		this.loadData();
	}

	onDayChange() {
		this.recomputeConflicts();
		this.scrollToFirstBlock();
	}

	// Switching server recording on/off always asks first: on means the server starts using the
	// single IPTV subscription, so the desktop script must stop.
	onRecordingToggle(event: MatSlideToggleChange) {
		const wanted = event.checked;
		event.source.checked = this.recordingEnabled;
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			width: '500px',
			data: {
				mode: 'USER_ACTION',
				type: 'custom-fields',
				message: this.translate.instant(wanted ? 'RECORDING_SCHEDULE_GANTT.CONFIRM_ON' : 'RECORDING_SCHEDULE_GANTT.CONFIRM_OFF'),
				fields: [],
			},
		});
		dialogRef
			.afterClosed()
			.pipe(take(1))
			.subscribe(confirmed => {
				if (!confirmed) {
					return;
				}
				const body: RecordingSettings = { maxConcurrentRecordings: this.maxConcurrentRecordings, recordingEnabled: wanted };
				this.savingSwitch = true;
				this.recordingService
					.updateSettings(body)
					.pipe(take(1))
					.subscribe({
						next: settings => {
							this.recordingEnabled = !!settings.recordingEnabled;
							this.savingSwitch = false;
						},
						error: () => {
							this.savingSwitch = false;
						},
					});
			});
	}

	onSegmentationToggle(event: MatSlideToggleChange) {
		const wanted = event.checked;
		this.savingSwitch = true;
		this.recordingService
			.updateSettings({ maxConcurrentRecordings: this.maxConcurrentRecordings, recordingEnabled: this.recordingEnabled, segmentationEnabled: wanted })
			.pipe(take(1))
			.subscribe({
				next: settings => {
					this.segmentationEnabled = settings.segmentationEnabled !== false;
					this.savingSwitch = false;
				},
				error: () => {
					event.source.checked = this.segmentationEnabled;
					this.savingSwitch = false;
				},
			});
	}

	onTranscriptionToggle(event: MatSlideToggleChange) {
		const wanted = event.checked;
		this.savingSwitch = true;
		this.recordingService
			.updateSettings({ maxConcurrentRecordings: this.maxConcurrentRecordings, recordingEnabled: this.recordingEnabled, transcriptionEnabled: wanted })
			.pipe(take(1))
			.subscribe({
				next: settings => {
					this.transcriptionEnabled = settings.transcriptionEnabled !== false;
					this.savingSwitch = false;
				},
				error: () => {
					event.source.checked = this.transcriptionEnabled;
					this.savingSwitch = false;
				},
			});
	}

	onAutoCreateClipsToggle(event: MatSlideToggleChange) {
		const wanted = event.checked;
		this.savingSwitch = true;
		this.recordingService
			.updateSettings({ maxConcurrentRecordings: this.maxConcurrentRecordings, recordingEnabled: this.recordingEnabled, autoCreateClipsEnabled: wanted })
			.pipe(take(1))
			.subscribe({
				next: settings => {
					this.autoCreateClipsEnabled = settings.autoCreateClipsEnabled === true;
					this.savingSwitch = false;
				},
				error: () => {
					event.source.checked = this.autoCreateClipsEnabled;
					this.savingSwitch = false;
				},
			});
	}

	onZoomChange() {
		this.scrollToFirstBlock();
	}

	// Г1: the "+" in a row's label column opens the full Medium edit dialog (the schedules
	// section lives there) already loaded for that medium - no separate "add schedule" popup to
	// keep in sync with the real one.
	addScheduleForMedium(row: MediumRow) {
		this.apiMedium
			.getMediumById(row.mediumId)
			.pipe(take(1))
			.subscribe(medium => {
				const dialogRef = this.dialog.open(MediumComponent, {
					width: '600px',
					data: {
						mode: 'EDIT',
						type: 'MEDIUM',
						typeWithScope: 'medium_clipping',
						item: medium,
					},
				});
				dialogRef
					.afterClosed()
					.pipe(take(1))
					.subscribe(() => this.loadData());
			});
	}

	get timelineMinWidthPx(): number {
		return 180 + TIMELINE_BASE_WIDTH_PX * this.zoom;
	}

	quarterStyle(minute: number) {
		return { left: (minute / MINUTES_IN_DAY) * 100 + '%' };
	}

	// Text drawn inside a block: the full range when it fits, just the start time when only that
	// fits, nothing at the whole-day zoom where a news slot is a few pixels wide (tooltip still has it).
	blockLabel(block: ScheduleBlock): string {
		const widthPx = ((block.endMinutes - block.startMinutes) / MINUTES_IN_DAY) * TIMELINE_BASE_WIDTH_PX * this.zoom;
		if (widthPx >= LABEL_RANGE_MIN_PX) {
			return this.formatTimeRange(block);
		}
		if (widthPx >= LABEL_START_MIN_PX) {
			return this.formatMinutes(block.startMinutes);
		}
		return '';
	}

	// When zoomed in, the earliest slot of the selected day may be off-screen; bring it into view
	// with an hour of context before it. At 1x the whole day fits, so reset to the start.
	private scrollToFirstBlock() {
		setTimeout(() => {
			const container = this.ganttScroll?.nativeElement;
			if (!container) {
				return;
			}
			if (this.zoom === 1) {
				container.scrollLeft = 0;
				return;
			}
			const starts = this.allSchedules
				.filter(schedule => (schedule.daysOfWeek || []).includes(this.selectedDay as any))
				.map(schedule => this.toBlock(schedule).startMinutes);
			if (starts.length === 0) {
				return;
			}
			const firstStart = Math.max(Math.min(...starts) - 60, 0);
			// A few px back so the hour label at the left edge isn't clipped by the sticky name column.
			container.scrollLeft = Math.max((firstStart / MINUTES_IN_DAY) * TIMELINE_BASE_WIDTH_PX * this.zoom - 12, 0);
		});
	}

	private loadData() {
		this.loading = true;
		forkJoin({
			schedules: this.apiRecordingSchedule.getAllSchedules(),
			settings: this.recordingService.getSettings(),
		})
			.pipe(take(1))
			.subscribe({
				next: ({ schedules, settings }) => {
					this.allSchedules = schedules;
					this.maxConcurrentRecordings = settings.maxConcurrentRecordings || 1;
					this.recordingEnabled = !!settings.recordingEnabled;
					this.segmentationEnabled = settings.segmentationEnabled !== false;
					this.transcriptionEnabled = settings.transcriptionEnabled !== false;
					this.autoCreateClipsEnabled = settings.autoCreateClipsEnabled === true;
					this.buildMediumRows();
					this.recomputeConflicts();
					this.loading = false;
				},
				error: () => {
					this.loading = false;
				},
			});
	}

	private buildMediumRows() {
		const byMedium = new Map<string, MediumRow>();
		this.allSchedules.forEach(schedule => {
			if (schedule.mediumId && !byMedium.has(schedule.mediumId)) {
				byMedium.set(schedule.mediumId, {
					mediumId: schedule.mediumId,
					name: this.utils.getParameterByLanguageScript(schedule, 'mediumName') || schedule.mediumNameLatin || '',
					type: schedule.mediumType || '',
				});
			}
		});
		this.mediumRows = Array.from(byMedium.values()).sort((a, b) => a.name.localeCompare(b.name));
	}

	schedulesForMedium(mediumId: string): ScheduleBlock[] {
		return this.allSchedules
			.filter(schedule => schedule.mediumId === mediumId && (schedule.daysOfWeek || []).includes(this.selectedDay as any))
			.map(schedule => this.toBlock(schedule));
	}

	blockStyle(block: ScheduleBlock) {
		return {
			left: (block.startMinutes / MINUTES_IN_DAY) * 100 + '%',
			width: Math.max(((block.endMinutes - block.startMinutes) / MINUTES_IN_DAY) * 100, 0.3) + '%',
			background: this.usernameColor(block.iptvUsername),
		};
	}

	// Deterministic string hash -> palette index, so the same username always gets the same
	// colour (here and in the legend below), with no colour stored anywhere.
	usernameColor(username: string | null): string {
		if (!username) {
			return NO_SUBSCRIPTION_COLOR;
		}
		let hash = 0;
		for (let i = 0; i < username.length; i++) {
			hash = (hash * 31 + username.charCodeAt(i)) | 0;
		}
		return USERNAME_PALETTE[Math.abs(hash) % USERNAME_PALETTE.length];
	}

	get usernameLegend(): { username: string; color: string }[] {
		const usernames = new Set<string>();
		this.allSchedules.forEach(schedule => {
			if (schedule.mediumIptvUsername) {
				usernames.add(schedule.mediumIptvUsername);
			}
		});
		return Array.from(usernames)
			.sort()
			.map(username => ({ username, color: this.usernameColor(username) }));
	}

	isInConflict(scheduleId: string): boolean {
		return this.conflictScheduleIds.has(scheduleId);
	}

	get conflictBands(): ConflictRange[] {
		return this.conflictRanges;
	}

	conflictBandStyle(range: ConflictRange) {
		return {
			left: (range.startMinutes / MINUTES_IN_DAY) * 100 + '%',
			width: ((range.endMinutes - range.startMinutes) / MINUTES_IN_DAY) * 100 + '%',
		};
	}

	formatTimeRange(block: ScheduleBlock): string {
		return `${this.formatMinutes(block.startMinutes)}–${this.formatMinutes(block.endMinutes)}`;
	}

	private formatMinutes(totalMinutes: number): string {
		const hours = Math.floor((totalMinutes % MINUTES_IN_DAY) / 60);
		const minutes = totalMinutes % 60;
		return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
	}

	private toBlock(schedule: ManagementRecordingScheduleWithMediumResponse): ScheduleBlock {
		const [hours, minutes] = (schedule.startTime || '00:00').split(':').map(part => parseInt(part, 10));
		const startMinutes = hours * 60 + minutes;
		return {
			id: schedule.id,
			mediumId: schedule.mediumId || '',
			startMinutes,
			endMinutes: startMinutes + (schedule.durationMinutes || 0),
			enabled: !!schedule.enabled,
			iptvUsername: schedule.mediumIptvUsername || null,
		};
	}

	// Sweep-line over the selected day's ENABLED schedules only (a disabled schedule holds no
	// capacity slot - same rule the backend's own conflict check uses, see
	// ManagementRecordingScheduleServiceImpl), run separately per IPTV username parsed from the
	// medium's link (В1): a physical subscription is one stream, so any overlap at all (more than
	// one at a time) within the SAME username is a conflict. A medium whose link has no
	// recognizable username is never grouped at all, so it never conflicts with anything.
	// max_concurrent_recordings plays no part in this any more (Oct 2026 admin tura, point 6) - it
	// is now only the server's flat safety ceiling on total simultaneous recordings, unrelated to
	// this per-subscription check.
	private recomputeConflicts() {
		const blocks = this.allSchedules
			.filter(schedule => schedule.enabled && (schedule.daysOfWeek || []).includes(this.selectedDay as any))
			.map(schedule => this.toBlock(schedule));

		const byUsername = new Map<string, ScheduleBlock[]>();
		blocks.forEach(block => {
			if (!block.iptvUsername) {
				return;
			}
			const group = byUsername.get(block.iptvUsername) || [];
			group.push(block);
			byUsername.set(block.iptvUsername, group);
		});

		const ranges: ConflictRange[] = [];
		const conflictingIds = new Set<string>();
		for (const group of byUsername.values()) {
			const groupRanges = this.overlapRanges(group);
			ranges.push(...groupRanges);
			group
				.filter(block => groupRanges.some(range => block.startMinutes < range.endMinutes && range.startMinutes < block.endMinutes))
				.forEach(block => conflictingIds.add(block.id));
		}

		this.conflictRanges = ranges;
		this.conflictScheduleIds = conflictingIds;
	}

	// Time ranges where more than one of these blocks is on air at once - a plain "concurrency > 1"
	// sweep, since the caller has already split blocks into subscription groups (each subscription
	// is one stream, so a second one at the same time is already too many).
	private overlapRanges(blocks: ScheduleBlock[]): ConflictRange[] {
		const events: { time: number; delta: number }[] = [];
		blocks.forEach(block => {
			events.push({ time: block.startMinutes, delta: 1 });
			events.push({ time: block.endMinutes, delta: -1 });
		});
		// At an equal timestamp, process an end (-1) before a start (+1): a schedule ending
		// exactly when another begins are not simultaneous (matches the backend's strict "<" overlap check).
		events.sort((a, b) => a.time - b.time || a.delta - b.delta);

		let count = 0;
		let rangeStart: number | null = null;
		const ranges: ConflictRange[] = [];
		for (const event of events) {
			const previousCount = count;
			count += event.delta;
			if (previousCount <= 1 && count > 1) {
				rangeStart = event.time;
			} else if (previousCount > 1 && count <= 1 && rangeStart !== null) {
				ranges.push({ startMinutes: rangeStart, endMinutes: event.time });
				rangeStart = null;
			}
		}
		return ranges;
	}

	private currentDayOfWeek(): string {
		const jsDayToCode = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
		return jsDayToCode[new Date().getDay()];
	}
}
